import type {
  AppointmentType,
  NotifySettings,
} from '@bb/domain/dist/types/Appointment';
import { HOME_INSPECTION_TYPES } from '@app/listing/constants';
import { AuthContext } from 'contexts';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { SearchResult as BuyerType } from '../../../../components/InviteBuyer';
import AppointmentBookingInternalContext from '../../AppointmentBookingInternalContext';
import { getBookForOptions } from '../helpers';
import type {
  BasicContactJson,
  CoopAdditionalDetails,
  InPersonBookForType,
} from '../types';

const CoopSelectContext = React.createContext<CoopSelectContextType>(
  {} as CoopSelectContextType,
);

export type CoopSelectContextType = {
  apptNotify: NotifySettings;
  setApptNotify: React.Dispatch<React.SetStateAction<NotifySettings>>;
  apptType: AppointmentType | null;
  setApptType: React.Dispatch<
    React.SetStateAction<AppointmentType | 'Information Request' | null>
  >;
  apptNote: string;
  setApptNote: React.Dispatch<React.SetStateAction<string>>;
  coop: BasicContactJson;
  setCoop: React.Dispatch<React.SetStateAction<BasicContactJson>>;
  coopAdditionalData: CoopAdditionalDetails | null;
  setCoopAdditionalData: React.Dispatch<
    React.SetStateAction<CoopAdditionalDetails | null>
  >;
  buyers: BuyerType[];
  setBuyers: React.Dispatch<React.SetStateAction<BuyerType[]>>;
  bookForOptions: InPersonBookForType[];
  bookFor: InPersonBookForType;
  setBookFor: React.Dispatch<React.SetStateAction<InPersonBookForType>>;
  showNote: boolean;
  setShowNote: React.Dispatch<React.SetStateAction<true>>;
};

export const CoopSelectContextProvider: React.FC<{
  onCoopSelect: () => void;
}> = ({ children, onCoopSelect }) => {
  const { auth } = useContext(AuthContext);
  const {
    isAdminWithAccess,
    authLastPhoneNumber,
    hasElevatedAccess,
    setAllowExceedMaxDuration,
    setConditionalBuyerAgreementData,
  } = useContext(AppointmentBookingInternalContext);

  const [apptNotify, setApptNotify] = useState<NotifySettings>({
    email: true,
    office: false,
    text: false,
  });
  const [apptNote, setApptNote] = useState('');
  const [apptType, setApptType] = useState<AppointmentType | null>(null);
  const [coop, setCoop] = useState<BasicContactJson>(null);
  const [coopAdditionalData, setCoopAdditionalData] =
    useState<CoopAdditionalDetails | null>(null);
  const [buyers, setBuyers] = useState<BuyerType[]>([]);

  const bookForOptions = useMemo(
    () => getBookForOptions({ auth, isAdminWithAccess, hasElevatedAccess }),
    [auth, isAdminWithAccess, hasElevatedAccess],
  );
  const [bookFor, setBookFor] = useState<InPersonBookForType>(
    bookForOptions[0],
  );
  const [showNote, setShowNote] = useState(false);

  useEffect(() => {
    if (bookFor === 'self') {
      setCoop({
        id: auth.id,
        name: `${auth.firstName} ${auth.lastName}`,
        firstName: auth.firstName,
        lastName: auth.lastName,
        email: auth.email,
        phone: authLastPhoneNumber?.number || auth.phone || '',
      });
      const authAddress = auth.org.addresses[+auth.location][+auth.address];
      setCoopAdditionalData({
        coopType: 'agent',
        notifications: auth.notifications,
        orgName: auth.org.name,
        orgAddress: authAddress?.address || '',
        orgPhone: authAddress?.phone || '',
        orgFeatures: auth.org.features,
      });
    }
  }, [auth, authLastPhoneNumber, bookFor, setCoop, setCoopAdditionalData]);

  useEffect(() => {
    if (coop) onCoopSelect();
  }, [coop, onCoopSelect]);

  useEffect(() => {
    if (!apptType) return;
    // Home inspections and appraisals may exceed the max duration configuration
    setAllowExceedMaxDuration(
      [
        ...HOME_INSPECTION_TYPES,
        'Home Inspection',
        'Appraisal',
        'Photographer',
        'Pest Control',
      ].includes(apptType),
    );
    const isBuyerShowing = [
      'Buyer/Broker',
      'Buyer Visit',
      'Revisit',
      'Dual Agency',
    ].includes(apptType);
    setConditionalBuyerAgreementData({ isBuyerShowing });
  }, [apptType, setAllowExceedMaxDuration, setConditionalBuyerAgreementData]);

  return (
    <CoopSelectContext.Provider
      value={{
        apptNotify,
        setApptNotify,
        apptType,
        setApptType,
        apptNote,
        setApptNote,
        coop,
        setCoop,
        coopAdditionalData,
        setCoopAdditionalData,
        buyers,
        setBuyers,
        bookForOptions,
        bookFor,
        setBookFor,
        showNote,
        setShowNote,
      }}
    >
      {children}
    </CoopSelectContext.Provider>
  );
};
export default CoopSelectContext;
